import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Download,
  Upload,
  CheckSquare,
  Square,
  Loader2,
  FileArchive,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { projectsApi, projectExportApi, organizationsApi, ExportPreview, ImportResult } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';

type ImportStep = 'upload' | 'preview' | 'mapping' | 'importing' | 'complete';

export function SettingsData() {
  const { user } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Export state
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Import state
  const [importStep, setImportStep] = useState<ImportStep>('upload');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<ExportPreview | null>(null);
  const [userMapping, setUserMapping] = useState<Record<string, string>>({});
  const [targetOrgId, setTargetOrgId] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // Get user's agency org for target org selection
  const agencyOrg = user?.organizations?.find((org: any) => org.type === 'AGENCY');

  // Fetch all projects for selection (including archived for agency admins)
  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects', 'export', 'all'],
    queryFn: () => projectsApi.list({ all: true }),
  });

  // Fetch client organizations for import target selection
  const { data: clientOrgs } = useQuery({
    queryKey: ['organizations', 'clients'],
    queryFn: () => organizationsApi.list({ type: 'CLIENT', all: true }),
  });

  const handleToggleProject = (projectId: string) => {
    const newSelected = new Set(selectedProjectIds);
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId);
    } else {
      newSelected.add(projectId);
    }
    setSelectedProjectIds(newSelected);
  };

  const handleSelectAll = () => {
    if (!projects) return;
    if (selectedProjectIds.size === projects.length) {
      setSelectedProjectIds(new Set());
    } else {
      setSelectedProjectIds(new Set(projects.map((p: any) => p.id)));
    }
  };

  const handleExport = async () => {
    if (selectedProjectIds.size === 0) return;

    setIsExporting(true);
    setExportError(null);

    try {
      const blob = await projectExportApi.exportProjects(Array.from(selectedProjectIds));

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `solidhub-export-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Clear selection after successful export
      setSelectedProjectIds(new Set());
    } catch (error: any) {
      setExportError(error.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportFile(file);
    setImportError(null);
    setImportStep('preview');

    try {
      const preview = await projectExportApi.previewImport(file);
      setImportPreview(preview);

      // Auto-map users by email match
      const autoMapping: Record<string, string> = {};
      for (const exportUser of preview.users) {
        const existingUser = preview.existingUsers.find(u => u.email === exportUser.email);
        if (existingUser) {
          autoMapping[exportUser.id] = existingUser.id;
        }
      }
      setUserMapping(autoMapping);

      // Set default target org
      if (clientOrgs && clientOrgs.length > 0) {
        setTargetOrgId(clientOrgs[0].id);
      } else if (agencyOrg) {
        setTargetOrgId(agencyOrg.id);
      }
    } catch (error: any) {
      setImportError(error.message || 'Failed to preview import file');
      setImportStep('upload');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleStartImport = async () => {
    if (!importFile || !targetOrgId) return;

    setIsImporting(true);
    setImportError(null);
    setImportStep('importing');

    try {
      const result = await projectExportApi.importProjects(
        importFile,
        targetOrgId,
        userMapping,
      );
      setImportResult(result);
      setImportStep('complete');
    } catch (error: any) {
      setImportError(error.message || 'Import failed');
      setImportStep('preview');
    } finally {
      setIsImporting(false);
    }
  };

  const resetImport = () => {
    setImportStep('upload');
    setImportFile(null);
    setImportPreview(null);
    setUserMapping({});
    setImportResult(null);
    setImportError(null);
  };

  const allSelected = projects && projects.length > 0 && selectedProjectIds.size === projects.length;
  const someSelected = selectedProjectIds.size > 0 && !allSelected;

  return (
    <div className="space-y-6">
      {/* Export Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <Download className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h2 className="text-heading-3 text-secondary">Export Projects</h2>
            <p className="text-sm text-grey-500 mt-1">
              Export project data including messages, tasks, and files
            </p>
          </div>
        </div>

        {exportError && (
          <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg flex items-center gap-2 text-error text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {exportError}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
          </div>
        ) : projects && projects.length > 0 ? (
          <>
            {/* Select All Header */}
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-grey-100">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 text-sm font-medium text-secondary hover:text-accent transition-colors"
              >
                {allSelected ? (
                  <CheckSquare className="w-4 h-4 text-accent" />
                ) : someSelected ? (
                  <div className="w-4 h-4 rounded border-2 border-accent bg-accent/20" />
                ) : (
                  <Square className="w-4 h-4 text-grey-400" />
                )}
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
              <span className="text-sm text-grey-500 mt-1">
                {selectedProjectIds.size} of {projects.length} selected
              </span>
            </div>

            {/* Project Grid */}
            <div className="max-h-96 overflow-y-auto mb-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {projects.map((project: any) => {
                  const isSelected = selectedProjectIds.has(project.id);
                  return (
                    <button
                      key={project.id}
                      onClick={() => handleToggleProject(project.id)}
                      className={cn(
                        'relative p-4 rounded-lg border-2 transition-all text-left',
                        isSelected
                          ? 'border-accent bg-accent/5'
                          : 'border-grey-200 hover:border-grey-300 hover:bg-grey-50'
                      )}
                    >
                      {/* Selection indicator */}
                      <div className="absolute top-2 right-2">
                        {isSelected ? (
                          <CheckSquare className="w-5 h-5 text-accent" />
                        ) : (
                          <Square className="w-5 h-5 text-grey-300" />
                        )}
                      </div>

                      <div className="pr-6">
                        <div className="font-medium text-secondary truncate mb-1">
                          {project.name}
                        </div>
                        {project.organization && (
                          <div className="text-xs text-grey-500 truncate mb-2">
                            {project.organization.name}
                          </div>
                        )}
                        {project.archivedAt && (
                          <span className="inline-block text-xs px-1.5 py-0.5 bg-grey-100 text-grey-500 rounded">
                            Archived
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Export Button */}
            <Button
              onClick={handleExport}
              disabled={selectedProjectIds.size === 0 || isExporting}
              className="w-full"
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileArchive className="w-4 h-4" />
              )}
              Export {selectedProjectIds.size > 0 ? `${selectedProjectIds.size} Project${selectedProjectIds.size > 1 ? 's' : ''}` : 'Selected Projects'}
            </Button>
          </>
        ) : (
          <div className="py-8 text-center text-grey-500">
            No projects available to export
          </div>
        )}
      </Card>

      {/* Import Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-grey-100 flex items-center justify-center">
            <Upload className="w-5 h-5 text-grey-500" />
          </div>
          <div>
            <h2 className="text-heading-3 text-secondary">Import Projects</h2>
            <p className="text-sm text-grey-500 mt-1">
              Import projects from a SolidHub export file
            </p>
          </div>
        </div>

        {importError && (
          <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg flex items-center gap-2 text-error text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {importError}
          </div>
        )}

        {importStep === 'upload' && (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed border-grey-200 rounded-lg p-8 text-center cursor-pointer hover:border-accent/50 hover:bg-accent/5 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 text-grey-400 mx-auto mb-3" />
              <p className="text-secondary font-medium mb-1">Click to select a file</p>
              <p className="text-sm text-grey-500 mt-1">or drag and drop a SolidHub export ZIP</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,application/zip"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}

        {importStep === 'preview' && importPreview && (
          <div className="space-y-4">
            {/* Import Summary */}
            <div className="p-4 bg-grey-50 rounded-lg">
              <h4 className="font-medium text-secondary mb-2">Import Preview</h4>
              <div className="text-sm text-grey-600 space-y-1">
                <p>Source: {importPreview.manifest.exportedBy.email}</p>
                <p>Exported: {new Date(importPreview.manifest.exportedAt).toLocaleString()}</p>
                <p>{importPreview.projects.length} project(s) to import</p>
              </div>
            </div>

            {/* Projects to Import */}
            <div>
              <h4 className="font-medium text-secondary mb-2">Projects</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {importPreview.projects.map((project) => (
                  <div key={project.id} className="p-3 bg-grey-50 rounded-lg">
                    <div className="font-medium text-secondary">{project.name}</div>
                    <div className="text-xs text-grey-500">
                      {project.memberCount} members, {project.messageCount} messages, {project.taskCount} tasks
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Unmapped Users Warning */}
            {importPreview.unmappedUsers.length > 0 && (
              <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-warning mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-medium text-secondary">Users Not Found</h4>
                    <p className="text-sm text-grey-600 mb-2">
                      These users from the export don't exist in this environment. Their content will be attributed to you.
                    </p>
                    <div className="text-xs text-grey-500 space-y-0.5">
                      {importPreview.unmappedUsers.slice(0, 5).map((u) => (
                        <div key={u.id}>{u.name || u.email}</div>
                      ))}
                      {importPreview.unmappedUsers.length > 5 && (
                        <div>+{importPreview.unmappedUsers.length - 5} more</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Target Organization */}
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Import Into Organization
              </label>
              <select
                value={targetOrgId}
                onChange={(e) => setTargetOrgId(e.target.value)}
                className="w-full px-3 py-2 border border-grey-200 rounded-lg text-secondary"
              >
                {agencyOrg && (
                  <option value={agencyOrg.id}>{agencyOrg.name} (Agency)</option>
                )}
                {clientOrgs?.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button variant="ghost" onClick={resetImport}>
                Cancel
              </Button>
              <Button onClick={handleStartImport} disabled={!targetOrgId}>
                Import Projects
              </Button>
            </div>
          </div>
        )}

        {importStep === 'importing' && (
          <div className="py-8 text-center">
            <Loader2 className="w-8 h-8 text-accent animate-spin mx-auto mb-4" />
            <p className="text-secondary font-medium">Importing projects...</p>
            <p className="text-sm text-grey-500 mt-1">This may take a few minutes</p>
          </div>
        )}

        {importStep === 'complete' && importResult && (
          <div className="space-y-4">
            {importResult.success ? (
              <div className="p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-secondary">Import Complete</h4>
                  <p className="text-sm text-grey-600">
                    Successfully imported {importResult.importedProjects.length} project(s)
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-error shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-secondary">Import Failed</h4>
                  <p className="text-sm text-grey-600">
                    Some errors occurred during import
                  </p>
                </div>
              </div>
            )}

            {/* Imported Projects */}
            {importResult.importedProjects.length > 0 && (
              <div>
                <h4 className="font-medium text-secondary mb-2">Imported Projects</h4>
                <div className="space-y-1">
                  {importResult.importedProjects.map((p) => (
                    <div key={p.newId} className="text-sm text-grey-600">
                      {p.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Warnings */}
            {importResult.warnings.length > 0 && (
              <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
                <h4 className="font-medium text-secondary mb-2">Warnings</h4>
                <div className="text-sm text-grey-600 space-y-1 max-h-32 overflow-y-auto">
                  {importResult.warnings.map((w, i) => (
                    <div key={i}>{w}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {importResult.errors.length > 0 && (
              <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
                <h4 className="font-medium text-secondary mb-2">Errors</h4>
                <div className="text-sm text-grey-600 space-y-1 max-h-32 overflow-y-auto">
                  {importResult.errors.map((e, i) => (
                    <div key={i}>{e}</div>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={resetImport}>
              Import More Projects
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
